import React, { useEffect, useState } from "react";
import Select from "react-select";
import "./PlansSection.scss";
import PlansTable from "./PlansTable";
import useFetch, { baseUrl } from "../../../hooks/useFetch";
import Tabination from "../../ReusableComponent/Tabination/Tabination";
import { customStyles } from "../../ReusableComponent/CustomStyle/customstyle";
import LoaderOverlap from "../../ReusableComponent/LoaderOverlap/LoaderOverlap";
import { dollarUS } from "../../../utils/helpers/string";

function PlansSection({ selectedPlanId, handleScrollToCheckout }) {
  const { isLoading, apiData } = useFetch(`${baseUrl}v2/challenges/`);

  const [filteredCategories, setFilteredCategories] = useState([]);
  const [uniqueAccountSizes, setUniqueAccountSizes] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [activeStepTab, setActiveStepTab] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedAccountSize, setSelectedAccountSize] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);

  console.log("selectedAccountSize", allPlans);

  // Calculate unique account balances
  const calculateUniqueAccountBalances = (categories, data) => {
    const accountBalances = [];
    const allPlansArray = [];
    categories.forEach((category) => {
      if (data[category]) {
        data[category].forEach((item) => {
          accountBalances.push(item.account_balance);
          allPlansArray.push(item);
        });
      }
    });
    return [...new Set(accountBalances)];
  };

  const allPlansArrayFunction = (categories, data) => {
    const allPlansArray = [];
    categories.forEach((category) => {
      if (data[category]) {
        data[category].forEach((item) => {
          allPlansArray.push(item);
        });
      }
    });
    return [...new Set(allPlansArray)];
  };

  // Filter categories based on selected step
  const filterCategoriesByStep = (step) => {
    if (!apiData) return [];
    return Object.keys(apiData)?.filter(
      (category) =>
        apiData[category]?.some((item) => item.steps === step) &&
        category !== "Free Trial"
    );
  };

  // Find the plan object by ID
  const findObjectById = (data, id) => {
    for (const key in data) {
      if (Array.isArray(data[key])) {
        for (const plan of data[key]) {
          if (plan.id === id) {
            return plan;
          }
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (selectedPlanId && apiData) {
      const plan = findObjectById(apiData, selectedPlanId);

      if (plan) {
        const step = plan.steps - 1;
        setActiveStepTab(step);

        const filtered = filterCategoriesByStep(plan.steps);
        setFilteredCategories(filtered);

        const categoryIndex = filtered?.findIndex((category) =>
          apiData[category].some((item) => item.id === selectedPlanId)
        );

        if (categoryIndex >= 0) {
          setActiveTab(categoryIndex);
        }

        const accountBalance = plan.account_balance;

        // Set selectedAccountSize only if it hasn't been set yet or it's different from the current value
        if (!selectedAccountSize || selectedAccountSize !== accountBalance) {
          setSelectedAccountSize(accountBalance);
        }

        const correspondingPlans = apiData[filtered[categoryIndex]]?.filter(
          (item) => item.account_balance === accountBalance
        );
        setSelectedPlans(correspondingPlans);
      }
    }
  }, [selectedPlanId, apiData]); // Only trigger this effect when selectedPlanId or apiData changes

  useEffect(() => {
    if (apiData && activeStepTab !== null) {
      const step = activeStepTab + 1;  // 1-based step number
  
      // Filter categories based on the current step
      const filtered = filterCategoriesByStep(step);
      setFilteredCategories(filtered);
  
      if (filtered.length > 0) {
        // Get unique account sizes for the filtered categories
        const accountSizes = calculateUniqueAccountBalances(filtered, apiData);
        setUniqueAccountSizes(accountSizes);
  
        // Get all plans for the filtered categories
        const plans = allPlansArrayFunction(filtered, apiData);
        setAllPlans(plans);
  
        // Now, check if the selectedAccountSize is valid in the new step
        if (selectedAccountSize && !accountSizes.includes(selectedAccountSize)) {
          // If the selectedAccountSize is not available, fallback to the first available account size
          const fallbackAccountSize = accountSizes[0];
          setSelectedAccountSize(fallbackAccountSize); // Set the fallback account size
        } else if (selectedAccountSize) {
          // If selectedAccountSize is valid, find its new index in the accountSizes array
          const newAccountSizeIndex = accountSizes.indexOf(selectedAccountSize);
          
          // Set the new account size index if needed (this will also update the account size)
          setActiveIndex(newAccountSizeIndex);
        }
  
        // After setting the account size, update the plans
        const correspondingPlans = apiData[filtered[0]]?.filter(
          (item) => item.account_balance === selectedAccountSize
        );
  
        // If no plans for the current account size, fallback to the first available account size
        if (!correspondingPlans || correspondingPlans.length === 0) {
          const fallbackAccountSize = accountSizes[0];
          setSelectedAccountSize(fallbackAccountSize); // Set fallback account size
          const fallbackPlans = apiData[filtered[0]]?.filter(
            (item) => item.account_balance === fallbackAccountSize
          );
          setSelectedPlans(fallbackPlans);
        } else {
          setSelectedPlans(correspondingPlans); // Set valid plans based on selected account size
        }
      }
    }
  }, [activeStepTab, apiData, selectedAccountSize]); // Trigger this effect when step, apiData, or selectedAccountSize changes
  
  // useEffect(() => {
  //   if (isLoading) {
  //     document.body.style.overflow = "hidden";
  //   } else {
  //     document.body.style.overflow = "";
  //   }
  //   return () => {
  //     document.body.style.overflow = "";
  //   };
  // }, [isLoading]);

  // Update the plans when account size changes
  useEffect(() => {
    if (selectedAccountSize && filteredCategories.length > 0) {
      const correspondingPlans = apiData[filteredCategories[activeTab]]?.filter(
        (item) => item.account_balance === selectedAccountSize
      );
      setSelectedPlans(correspondingPlans);
    }
  }, [selectedAccountSize, filteredCategories, activeTab, apiData]);

  const [activeIndex, setActiveIndex] = useState(0); // Track active slide/tab
  const [swiperRef, setSwiperRef] = useState(null);

  const handleAccountSizeSelect = (index) => {
    const accountSize = uniqueAccountSizes[index];
    setSelectedAccountSize(accountSize); // Set new account size
    setActiveIndex(index);
    if (swiperRef) {
      swiperRef.slideTo(index); // Move Swiper to the correct slide
    }
  };

  useEffect(() => {
    if (swiperRef) {
      swiperRef.slideTo(activeIndex);
    }
  }, [activeIndex, swiperRef, activeStepTab]);

  const handlePlanTabChange = (index) => {
    setActiveTab(index);
  };

  // if (isLoading) {
  //   return <LoaderOverlap />;
  // }

  useEffect(() => {
    setActiveTab(0);
    if (selectedAccountSize && filteredCategories.length > 0) {
      const correspondingPlans = apiData[filteredCategories[activeTab]]?.filter(
        (item) => item.account_balance === selectedAccountSize
      );
      setSelectedPlans(correspondingPlans);
    }
  }, [activeStepTab]);

  return (
    <div className="ps_wrapper">
      {isLoading && <LoaderOverlap />}
      <div className="ps_intro">
        <h1>
          Still Not Sure?{" "}
          <span className="gradientText">Check out our other plans</span>
        </h1>
        <p>
          Choose the evaluation based on your preference to become an Alpha
          Trader.
        </p>

        {/* Step Selection */}
        <div className="plan_select step_select">
          {/* <p>Select Your Step</p> */}
          <Tabination
            tabItems={[
              { name: "1 Step" },
              { name: "2 Step" },
              { name: "3 Step" },
            ]}
            activeTab={activeStepTab}
            setActiveTab={setActiveStepTab}
            variant={2}
          />
          {/* <div className="stepSelector">
            <Select
              isSearchable={false}
              placeholder="Select a Step"
              className="react-select-container"
              classNamePrefix="react-select"
              options={[
                { label: "1 Step", value: 0 },
                { label: "2 Step", value: 1 },
                { label: "3 Step", value: 2 },
              ]}
              value={[
                { label: "1 Step", value: 0 },
                { label: "2 Step", value: 1 },
                { label: "3 Step", value: 2 },
              ].find((item) => item.value === activeStepTab)}
              onChange={(selectedOption) =>
                setActiveStepTab(selectedOption.value)
              }
              styles={customStyles(60)}
            />
          </div> */}
        </div>

        {/* Plan Selection */}
        <div className="plan_select plan_selector_container">
          {/* <p>Select Your Plan</p> */}
          <Tabination
            tabItems={filteredCategories?.map((plan) => ({ name: plan }))}
            activeTab={activeTab}
            setActiveTab={handlePlanTabChange}
            variant={2}
          />
          {/* <div className="planMobileSelector">
            <Select
              isSearchable={false}
              placeholder="Select a Plan"
              className="react-select-container"
              classNamePrefix="react-select"
              options={filteredCategories?.map((plan, index) => ({
                label: plan,
                value: index,
              }))}
              value={filteredCategories
                .map((plan, index) => ({
                  label: plan,
                  value: index,
                }))
                .find((item) => item.value === activeTab)}
              onChange={(selectedOption) => setActiveTab(selectedOption.value)}
              styles={customStyles(60)}
            />
          </div> */}
        </div>

        {/* Account Size Selection */}
        <div className="plan_select account_size_select">
          {/* <p>Select Account Size</p> */}
          <Tabination
            tabItems={uniqueAccountSizes.map((size) => ({
              name: `${dollarUS(size)}`,
            }))}
            activeTab={activeIndex}
            setActiveTab={handleAccountSizeSelect}
            variant={2}
          />
          {/* <div className="planMobileSelector">
            <Select
              placeholder="Select Account"
              className="react-select-container"
              classNamePrefix="react-select"
              value={uniqueAccountSizes
                .map((size) => ({
                  label: `$${size.toLocaleString()}`,
                  value: size,
                }))
                .find((item) => item.value === selectedAccountSize)}
              options={uniqueAccountSizes.map((size) => ({
                label: `$${size.toLocaleString()}`,
                value: size,
              }))}
              onChange={(selectedOption) =>
                setSelectedAccountSize(selectedOption.value)
              }
              styles={customStyles(60)}
              isSearchable={false}
            />
          </div> */}
        </div>
      </div>

      {selectedPlans?.length > 0 && (
        <PlansTable
          handleScrollToCheckout={handleScrollToCheckout}
          plansData={selectedPlans}
          activeStep={activeStepTab + 1}
          selectedCategory={filteredCategories[activeTab]}
          activeTab={activeTab}
          allPlans={allPlans}
          uniqueAccountSizes={uniqueAccountSizes}
          handleAccountSizeSelect={handleAccountSizeSelect}
          setSwiperRef={setSwiperRef}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
        />
      )}
    </div>
  );
}

export default PlansSection;
